import { effectivePermissionSetHas, type UserRole } from '@utazzo/shared';
import { Navigate, Outlet } from 'react-router-dom';

import { homePathForRole } from '@/app/home-path';
import { useCurrentUser } from '@/features/auth/use-auth';
import { useMyEffectivePermissions } from '@/features/my-profile/use-my-profile';

interface RequireRoleProps {
  /// Roles that may view the wrapped routes. The caller's `role` must be
  /// in this list - everything else is bounced. Omit to gate purely by
  /// `permission`.
  allow?: ReadonlyArray<UserRole>;
  /// Permission code from the role/permission DB (ADR-0039 Phase 3),
  /// e.g. "customer.read". When set, the user's effective permission set
  /// must contain this code. ADMIN bypasses (admins always pass at the
  /// UI layer). Users with no role-table row yet fall through (legacy
  /// `@Roles` enum path still works).
  permission?: string;
  /// Where to send users that don't pass. Defaults to the role home so a
  /// SALES_AGENT typing `/users` lands on Lead Inbox (dashboard for
  /// everyone else) instead of a 404 / blank page.
  fallback?: string;
  /// Specific user emails (case-insensitive) to deny even when they satisfy
  /// `allow` / `permission`. One-off per-person exclusions; pair with the
  /// nav item's `hideForEmails` so the link is hidden too.
  denyEmails?: ReadonlyArray<string>;
  /// Roles to deny even when they satisfy `allow` / `permission`. The route
  /// counterpart of the nav item's `hideForRoles`: a role that happens to hold
  /// the permission (so the permission gate would pass) is still bounced here,
  /// so the page is not reachable by typing the URL. Pair with `hideForRoles`.
  denyRoles?: ReadonlyArray<UserRole>;
}

/**
 * Route guard for restricted areas. Wraps a route subtree; the user is
 * redirected to `fallback` (default: role home) when:
 *   - `allow` is set and the user's role isn't in the list, OR
 *   - `permission` is set, the user isn't ADMIN, and their effective
 *     permission set doesn't include the code.
 *
 * Pair with sidebar-level filtering in AppShell so the operator never
 * sees the link in the first place - this guard is the second line of
 * defence behind that.
 */
export const RequireRole = ({
  allow,
  permission,
  fallback,
  denyEmails,
  denyRoles,
}: RequireRoleProps): React.JSX.Element => {
  const user = useCurrentUser();
  const role = user?.role;
  const effectivePermissions = useMyEffectivePermissions();
  const bounceTo = fallback ?? homePathForRole(role);

  // Per-person deny list - bounce named emails regardless of role/permission.
  if (denyEmails && user?.email && denyEmails.includes(user.email.trim().toLowerCase())) {
    return <Navigate to={bounceTo} replace />;
  }

  // Per-role deny list - bounce these roles even if they hold the permission
  // (the route counterpart of the nav item's `hideForRoles`).
  if (denyRoles && role && denyRoles.includes(role)) {
    return <Navigate to={bounceTo} replace />;
  }

  // Permission-loading race. On a cold tab (e.g. window.open to
  // /itineraries/new from a lead page) the effective-permission set needs a
  // round-trip before it resolves. `undefined` means the React Query call
  // hasn't returned yet - holding the route (rendering nothing) avoids a
  // false redirect to fallback before permissions even arrive. `null` (the
  // separate "loaded but no grants" signal from useMyEffectivePermissions)
  // falls through to the gate below and correctly redirects.
  if (permission && effectivePermissions === undefined) return <></>;

  // Role gate.
  if (allow && (!role || !allow.includes(role))) {
    return <Navigate to={bounceTo} replace />;
  }

  // Permission gate - exact codes only; no ADMIN bypass.
  if (permission) {
    if (!(effectivePermissions instanceof Set)) {
      return <Navigate to={bounceTo} replace />;
    }
    if (!effectivePermissionSetHas(effectivePermissions, permission)) {
      return <Navigate to={bounceTo} replace />;
    }
  }

  return <Outlet />;
};
